import { useCallback, useEffect, useRef, useState } from "react";
import {
  createThrottledPublisher,
  publishSignal,
  startHeartbeat,
  subscribeSignals,
  type PresentAction,
  type PresentSenderRole,
  type PresentSignal,
  type PresentSignalInput,
  type SurfRealtimeStatus,
  PEER_TIMEOUT_MS,
} from "@vida/surf-core";
import { createPresentChannelConfig } from "../../services/surfClientConfig";

type Options = {
  sessionId: string | null;
  deckId: string;
  role: PresentSenderRole;
  /** Called for every inbound signal that originates from the *peer* role. */
  onSignal: (signal: PresentSignal) => void;
  /** When set (phone/remote), republishes this signal on the heartbeat cadence. */
  getHeartbeatSignal?: () => PresentSignalInput | null;
};

export type RemoteChannel = {
  status: SurfRealtimeStatus;
  /** True while the peer device has been heard from within PEER_TIMEOUT_MS. */
  peerPresent: boolean;
  /** Last publish/subscribe failure surfaced by the channel (null when healthy). */
  lastError: string | null;
  sendGoto: (slideIndex: number) => void;
  sendBlank: () => void;
  sendUnblank: () => void;
  sendPointer: (x: number | null, y: number | null) => void;
  sendPing: (slideIndex?: number | null) => void;
  sendEnd: () => void;
};

function describeChannelError(err: unknown): string {
  if (err == null) return "errore sconosciuto";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (Array.isArray(err)) {
    const messages = err
      .map((e) => (e && typeof e === "object" && "message" in e ? `${(e as { message?: unknown }).message ?? ""}` : ""))
      .filter((m) => m !== "");
    if (messages.length > 0) return messages.join("; ");
  }
  if (typeof err === "object" && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message !== "") return message;
  }
  return "errore canale";
}

export function useRemoteChannel({
  sessionId,
  deckId,
  role,
  onSignal,
  getHeartbeatSignal,
}: Options): RemoteChannel {
  const [status, setStatus] = useState<SurfRealtimeStatus>("offline");
  const [peerPresent, setPeerPresent] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const cfgRef = useRef(
    createPresentChannelConfig((err, phase) => {
      const message = describeChannelError(err);
      console.warn(`[present] ${phase} error`, err);
      setLastError(`${phase === "publish" ? "invio" : "ricezione"}: ${message}`);
    }),
  );
  const onSignalRef = useRef(onSignal);
  const heartbeatRef = useRef(getHeartbeatSignal);
  const peerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerPublisherRef = useRef<ReturnType<typeof createThrottledPublisher> | null>(null);

  onSignalRef.current = onSignal;
  heartbeatRef.current = getHeartbeatSignal;

  if (!pointerPublisherRef.current) {
    pointerPublisherRef.current = createThrottledPublisher(cfgRef.current);
  }

  const markPeerSeen = useCallback(() => {
    setPeerPresent(true);
    if (peerTimerRef.current) clearTimeout(peerTimerRef.current);
    peerTimerRef.current = setTimeout(() => setPeerPresent(false), PEER_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus("offline");
      return;
    }
    const cfg = cfgRef.current;
    const stop = subscribeSignals(
      cfg,
      sessionId,
      (signal) => {
        // Ignore our own echoes; only react to the peer role.
        if (signal.senderRole === role) return;
        markPeerSeen();
        // A delivered signal means the channel works: clear any stale error badge.
        setLastError(null);
        onSignalRef.current(signal);
      },
      { onStatus: setStatus },
    );
    return () => {
      stop();
      if (peerTimerRef.current) clearTimeout(peerTimerRef.current);
      setPeerPresent(false);
    };
  }, [sessionId, role, markPeerSeen]);

  // Heartbeat (phone/remote): keep the display aligned + advertise presence.
  useEffect(() => {
    if (!sessionId || !heartbeatRef.current) return;
    const cfg = cfgRef.current;
    const stop = startHeartbeat(cfg, () => heartbeatRef.current?.() ?? null);
    return stop;
  }, [sessionId]);

  const fire = useCallback(
    (action: PresentAction, extra?: Partial<PresentSignalInput>) => {
      if (!sessionId) return;
      const input: PresentSignalInput = {
        sessionId,
        deckId,
        action,
        senderRole: role,
        ...extra,
      };
      void publishSignal(cfgRef.current, input).catch((err) => {
        console.warn("[present] publish failed", err);
        setLastError(`invio: ${describeChannelError(err)}`);
      });
    },
    [sessionId, deckId, role],
  );

  const sendGoto = useCallback(
    (slideIndex: number) => fire("goto", { slideIndex }),
    [fire],
  );
  const sendBlank = useCallback(() => fire("blank"), [fire]);
  const sendUnblank = useCallback(() => fire("unblank"), [fire]);
  const sendPing = useCallback(
    (slideIndex?: number | null) => fire("ping", { slideIndex: slideIndex ?? null }),
    [fire],
  );
  const sendEnd = useCallback(() => fire("end"), [fire]);

  const sendPointer = useCallback(
    (x: number | null, y: number | null) => {
      if (!sessionId) return;
      pointerPublisherRef.current?.send({
        sessionId,
        deckId,
        action: "pointer",
        senderRole: role,
        pointerX: x,
        pointerY: y,
      });
    },
    [sessionId, deckId, role],
  );

  return {
    status,
    peerPresent,
    lastError,
    sendGoto,
    sendBlank,
    sendUnblank,
    sendPointer,
    sendPing,
    sendEnd,
  };
}
